import React from 'react';
import { Bell, BellRing, X, CheckSquare, MessageSquare, Star } from 'lucide-react';
import { G1, GG } from '../../config/styles';

// ── Toast ──────────────────────────────────
export const ToastContainer = ({ toasts, removeToast, isRTL }) => {
  if (!toasts.length) return null;
  const side = isRTL ? 'left' : 'right';
  const anim = isRTL ? 'slideInLeft' : 'slideInRight';
  const cfg = {
    success: { bg: '#F0FDF4', border: '#86EFAC', color: '#15803D', icon: '✅' },
    error:   { bg: '#FEF2F2', border: '#FECACA', color: '#DC2626', icon: '❌' },
    warning: { bg: '#FFFBEB', border: '#FDE68A', color: '#B45309', icon: '⚠️' },
    info:    { bg: '#EFF6FF', border: '#BFDBFE', color: '#1D4ED8', icon: 'ℹ️' },
  };
  return (
    <div style={{ position: 'fixed', bottom: '88px', [side]: '20px', zIndex: 200, display: 'flex', flexDirection: 'column', gap: '8px', maxWidth: '310px', pointerEvents: 'none' }}>
      {toasts.map(t => {
        const c = cfg[t.type] || cfg.success;
        return (
          <div key={t.id} style={{ background: c.bg, border: `1.5px solid ${c.border}`, borderRadius: '12px', padding: '12px 14px', color: c.color, fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 6px 20px rgba(0,0,0,0.13)', pointerEvents: 'all', animation: `${anim} .35s cubic-bezier(.34,1.56,.64,1)` }}>
            <span style={{ fontSize: '16px', flexShrink: 0 }}>{c.icon}</span>
            <span style={{ flex: 1, lineHeight: '1.4' }}>{t.msg}</span>
            <button onClick={() => removeToast(t.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: c.color, padding: 0, display: 'flex', opacity: 0.55, flexShrink: 0 }}><X size={13} /></button>
          </div>
        );
      })}
    </div>
  );
};

// ── Notification Panel ──────────────────────
export const NotifPanel = ({ pending, messages, customers, courses, reviews = [], onApproveReview, onDeleteReview, onClose, onApprove, onReject, isRTL, lang, t }) => {
  const side = isRTL ? 'left' : 'right';
  const pendingReviews = reviews.filter(r => r.is_approved !== true);
  const totalCount = pending.length + pendingReviews.length + messages.filter(m => !m.is_read).length;
  return (
    <div style={{ position: 'fixed', top: '70px', [side]: '16px', width: '340px', maxWidth: '92vw', background: 'white', borderRadius: '18px', boxShadow: '0 12px 40px rgba(0,0,0,0.18)', zIndex: 150, overflow: 'hidden', animation: 'fadeInDown .25s ease' }}>
      <div style={{ background: G1, padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'white', fontWeight: 'bold', fontSize: '14px' }}>
          <Bell size={16} /> {lang === 'ar' ? 'الإشعارات' : 'Notifications'}
          {totalCount > 0 && (
            <span style={{ background: '#EF4444', color: 'white', borderRadius: '20px', padding: '1px 7px', fontSize: '11px', fontWeight: 'bold' }}>
              {totalCount}
            </span>
          )}
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', display: 'flex' }}><X size={17} /></button>
      </div>

      <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
        {/* Pending Payments */}
        {pending.length > 0 && (
          <div style={{ padding: '14px 16px 0' }}>
            <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#EF4444', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#EF4444', display: 'inline-block' }} />
              {lang === 'ar' ? `مدفوعات في الانتظار (${pending.length})` : `Pending Payments (${pending.length})`}
            </div>
            {pending.map(pay => {
              const cust = customers.find(c => c.id === pay.customer_id);
              const course = courses.find(c => c.id === pay.course_id);
              return (
                <div key={pay.id} style={{ background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: '10px', padding: '10px 12px', marginBottom: '8px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#1F2937', marginBottom: '4px' }}>{cust?.name} — {lang === 'ar' ? course?.name_ar : course?.name_en}</div>
                  <div style={{ fontSize: '11px', color: '#6B7280', marginBottom: '8px' }}>
                    {pay.amount} جنيه ·{' '}
                    <span style={{ background: pay.method === 'card' ? '#DBEAFE' : '#FEF3C7', color: pay.method === 'card' ? '#1D4ED8' : '#92400E', padding: '1px 6px', borderRadius: '4px', fontWeight: 'bold' }}>
                      {pay.method === 'card' ? '💳 فيزا' : pay.method === 'vodafone' ? '📱 فودافون' : '🏦 تحويل'}
                    </span>
                    {pay.receipt_note && <span> · {pay.receipt_note}</span>}
                  </div>

                  {/* كارت = أوتوماتيك عن طريق Webhook بس */}
                  {pay.method === 'card' ? (
                    <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '6px', padding: '7px 10px', fontSize: '11px', color: '#1D4ED8', display: 'flex', alignItems: 'center', gap: '5px' }}>
                      ⏳ {lang === 'ar' ? 'في انتظار تأكيد Paymob أوتوماتيك' : 'Waiting for Paymob auto-confirmation'}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button onClick={() => onApprove(pay)} style={{ flex: 1, padding: '5px', background: GG, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px' }}>
                        <CheckSquare size={11} /> {lang === 'ar' ? 'تفعيل' : 'Approve'}
                      </button>
                      <button onClick={() => onReject(pay.id)} style={{ flex: 1, padding: '5px', background: '#EF4444', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}>
                        {lang === 'ar' ? 'رفض' : 'Reject'}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Pending Reviews — آراء مستنية الموافقة */}
        {pendingReviews.length > 0 && (
          <div style={{ padding: '14px 16px 0' }}>
            <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#F59E0B', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <Star size={11} fill="#F59E0B" color="#F59E0B" />
              {lang === 'ar' ? `تقييمات في الانتظار (${pendingReviews.length})` : `Pending Reviews (${pendingReviews.length})`}
            </div>
            {pendingReviews.map(rv => {
              const course = courses.find(c => c.id === rv.course_id);
              return (
                <div key={rv.id} style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '10px', padding: '10px 12px', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#1F2937' }}>{rv.student_name || (lang === 'ar' ? 'طالب' : 'Student')}</span>
                    <span style={{ display: 'flex', gap: '1px' }}>
                      {[1, 2, 3, 4, 5].map(n => (
                        <Star key={n} size={12} fill={n <= rv.rating ? '#F59E0B' : 'none'} color={n <= rv.rating ? '#F59E0B' : '#D1D5DB'} />
                      ))}
                    </span>
                  </div>
                  <div style={{ fontSize: '11px', color: '#92400E', marginBottom: '6px' }}>{lang === 'ar' ? course?.name_ar : course?.name_en}</div>
                  {rv.comment_ar && <div style={{ fontSize: '11px', color: '#6B7280', lineHeight: '1.5', marginBottom: '8px', background: 'white', padding: '6px 8px', borderRadius: '6px' }}>{rv.comment_ar}</div>}
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button onClick={() => onApproveReview && onApproveReview(rv.id)} style={{ flex: 1, padding: '5px', background: GG, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px' }}>
                      <CheckSquare size={11} /> {lang === 'ar' ? 'موافقة' : 'Approve'}
                    </button>
                    <button onClick={() => onDeleteReview && onDeleteReview(rv.id)} style={{ flex: 1, padding: '5px', background: '#EF4444', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}>
                      {lang === 'ar' ? 'رفض' : 'Reject'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Contact Messages */}
        {messages.length > 0 && (
          <div style={{ padding: '14px 16px' }}>
            <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#667eea', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <MessageSquare size={11} />
              {lang === 'ar' ? `رسائل التواصل (${messages.length})` : `Contact Messages (${messages.length})`}
            </div>
            {messages.slice(0, 5).map(msg => (
              <div key={msg.id} style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '10px', padding: '10px 12px', marginBottom: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '3px' }}>
                  <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#1F2937' }}>{msg.name}</span>
                  <span style={{ fontSize: '10px', color: '#9CA3AF' }}>{msg.created_at ? new Date(msg.created_at).toLocaleDateString(lang === 'ar' ? 'ar-EG' : 'en') : ''}</span>
                </div>
                {msg.service && <div style={{ fontSize: '10px', color: '#667eea', marginBottom: '3px', background: '#EEF2FF', display: 'inline-block', padding: '1px 6px', borderRadius: '4px' }}>{msg.service}</div>}
                <div style={{ fontSize: '11px', color: '#6B7280', lineHeight: '1.4' }}>{(msg.message || '').slice(0, 80)}{(msg.message || '').length > 80 ? '...' : ''}</div>
                <div style={{ fontSize: '10px', color: '#9CA3AF', marginTop: '3px' }}>📱 {msg.phone || '—'} · ✉️ {msg.email || '—'}</div>
              </div>
            ))}
            {messages.length > 5 && (
              <div style={{ textAlign: 'center', fontSize: '11px', color: '#9CA3AF', padding: '4px' }}>+{messages.length - 5} {lang === 'ar' ? 'رسالة أخرى' : 'more messages'}</div>
            )}
          </div>
        )}

        {pending.length === 0 && messages.length === 0 && pendingReviews.length === 0 && (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: '#9CA3AF' }}>
            <Bell size={32} style={{ opacity: 0.3, margin: '0 auto 10px', display: 'block' }} />
            <div style={{ fontSize: '13px' }}>{lang === 'ar' ? 'لا توجد إشعارات جديدة' : 'No new notifications'}</div>
          </div>
        )}
      </div>
    </div>
  );
};
